import { Ghost, Phone, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

// Auto-expires at midnight local time on Nov 1. Update EXPIRES_AT (or delete
// this component from Navigation.jsx) after the season is over.
const EXPIRES_AT = new Date("2026-11-01T00:00:00");
// Oct 31 end-of-day — used to distinguish "last day" from "ends tomorrow".
const LAST_DAY = new Date("2026-10-31T00:00:00");

function computeCountdown(now = new Date()) {
  if (now >= EXPIRES_AT) return null;
  // Difference in whole calendar days between today's local date and Oct 31.
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfLastDay = new Date(LAST_DAY.getFullYear(), LAST_DAY.getMonth(), LAST_DAY.getDate());
  const dayDiff = Math.round((startOfLastDay - startOfToday) / (24 * 60 * 60 * 1000));
  if (dayDiff <= 0) return { label: "Ends tonight", urgent: true };
  if (dayDiff === 1) return { label: "Ends tomorrow", urgent: true };
  return { label: `${dayDiff} days left`, urgent: dayDiff <= 3 };
}

export default function HalloweenBanner() {
  const [active, setActive] = useState(() => new Date() < EXPIRES_AT);
  const [countdown, setCountdown] = useState(() => computeCountdown());

  useEffect(() => {
    if (!active) return;
    // Refresh every minute so the "days left" pill stays accurate — and so
    // the banner cleanly disappears when the clock passes Nov 1 midnight.
    const t = setInterval(() => {
      const now = new Date();
      if (now >= EXPIRES_AT) { setActive(false); return; }
      setCountdown(computeCountdown(now));
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [active]);

  if (!active) return null;

  const goToContact = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.dispatchEvent(
        new CustomEvent("prefill-contact", {
          detail: {
            service: "Halloween Makeup Special",
            message:
              "Hi Bella — I'd like to book the Halloween Makeup Special.\n\nEvent date: __\nLook I'm going for: __\nPreferred time: __\n\nThank you!",
          },
        }),
      );
    } else {
      window.location.href = "/#contact";
    }
  };

  return (
    <div
      data-testid="halloween-banner"
      className="relative overflow-hidden bg-gradient-to-r from-[#3a0e5b] via-[#8b1e00] to-[#f57c00] text-[#FFF3D6] border-b border-[#FFB74D]/50"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-24 w-1/3 bg-gradient-to-r from-transparent via-[#FFE082]/25 to-transparent skew-x-12 animate-[shine_5s_ease-in-out_infinite]"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-2.5 md:py-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-center">
        <span className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#FFE082] font-medium">
          <Ghost className="w-3.5 h-3.5" aria-hidden="true" />
          Limited-time
        </span>
        <span className="text-sm md:text-base font-light">
          <span className="font-serif italic text-[#FFE082]">Halloween Makeup Special</span>{" "}
          — book by Oct 31.
        </span>
        {countdown && (
          <span
            data-testid="halloween-banner-countdown"
            className={[
              "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] md:text-xs uppercase tracking-[0.2em] font-medium border transition-colors",
              countdown.urgent
                ? "bg-[#FFE082] text-[#3a0e5b] border-[#FFE082] animate-pulse"
                : "bg-transparent text-[#FFE082] border-[#FFE082]/50",
            ].join(" ")}
          >
            <Clock className="w-3 h-3" aria-hidden="true" />
            {countdown.label}
          </span>
        )}
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <a
            href="tel:9162435514"
            data-testid="halloween-banner-call-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#FFE082] text-[#3a0e5b] text-xs uppercase tracking-wider hover:bg-[#FFF3D6] font-medium transition-colors"
          >
            <Phone className="w-3.5 h-3.5" /> Call 916-243-5514
          </a>
          <a
            href="/#contact"
            onClick={goToContact}
            data-testid="halloween-banner-contact-btn"
            className="text-xs uppercase tracking-wider text-[#FFE082] hover:text-[#FFF3D6] underline underline-offset-4"
          >
            Or schedule via contact →
          </a>
          <Link
            to="/blog/halloween-makeup-special-sacramento"
            data-testid="halloween-banner-details-btn"
            className="text-xs uppercase tracking-wider text-[#FFE082]/80 hover:text-[#FFF3D6] underline underline-offset-4"
          >
            Details →
          </Link>
        </div>
      </div>
    </div>
  );
}
